<template>
  <Container>
    <HeroCard
      :heading="t('jc.view.participate.hero.title')"
      :paragraph="t('jc.view.participate.hero.text')"
      :image="{
        src: 'images/nature/sunshine.png',
        alt: t('jc.view.participate.hero.banner.alt'),
      }"
      :float="{
        src: 'images/skin_renders/compositions/participate.png',
        alt: t('jc.view.participate.hero.float.alt'),
      }"
      :label="t('jc.view.participate.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container>
    <ContentCard
      :heading="t('jc.view.participate.team.title')"
      :paragraph="t('jc.view.participate.team.text')"
    >
      <div class="flex">
        <NuxtLink
          :to="localePath({ name: 'team' })"
          class="bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white
            text-center rounded py-2 px-4 flex flex-row gap-3 font-semibold mr-auto"
        >
          <font-awesome-icon
            :icon="['fas', 'users']"
            fixed-width
            size="lg"
          />
          <span>{{ t('jc.view.participate.team.button.label') }}</span>
        </NuxtLink>
      </div>
    </ContentCard>
  </Container>

  <Container>
    <ContentCard
      :heading="t('jc.view.participate.creator.title')"
      :paragraph="t('jc.view.participate.creator.text')"
    >
      <div class="flex">
        <NuxtLink
          :to="localePath({ name: 'creator' })"
          class="bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white
            text-center rounded py-2 px-4 flex flex-row gap-3 font-semibold mr-auto"
        >
          <font-awesome-icon
            :icon="['fas', 'hand-holding-dollar']"
            fixed-width
            size="lg"
          />
          <span>{{ t('jc.view.participate.creator.button.label') }}</span>
        </NuxtLink>
      </div>
    </ContentCard>
  </Container>

  <Container>
    <div
      class="flex flex-col gap-16"
    >
      <template
        v-for="[departmentKey, roles] in departments"
        :key="departmentKey"
      >
        <div
          class="flex flex-col gap-8"
          :class="['department-' + DepartmentData[departmentKey].nameKey]"
        >
          <Heading
            :icon="DepartmentData[departmentKey].icon"
            :label="t(`jc.department.${departmentKey}.label`)"
          />
          <div
            class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
          >
            <template
              v-for="role in roles"
              :key="role"
            >
              <RoleCard
                :user-role="role"
              />
            </template>
          </div>
        </div>
      </template>
    </div>
  </Container>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { Department } from '~/composables/charon'
import { UserRole, DepartmentData, UserRoleData } from '~/composables/charon'

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

definePageMeta({
  nameKey: 'participate',
  head: {
    imageUrl: '/images/view/participate/meta_preview.png',
  },
})

const UserRoles = ref([
  // development
  UserRole.GAME_DEVELOPER,
  UserRole.INFRA_DEVELOPER,
  UserRole.WEB_DEVELOPER,
  // creative
  UserRole.BUILDER,
  UserRole.GRAPHIC_ARTIST,
  UserRole.VIDEO_PRODUCER,
  UserRole.AUDIO_DESIGNER,
  UserRole.GAME_DESIGNER,
  // promotion
  UserRole.PROMOTER,
  UserRole.EDITOR,
  // support
  UserRole.SUPPORTER,
])
// computed: the props user role keys grouped by there corresponding department key
const departments = computed(() => {
  const departments = UserRoles.value.reduce<Partial<Record<Department, UserRole[]>>>((acc, role) => {
    const dep = UserRoleData[role].department
    if (!acc[dep]) acc[dep] = []
    acc[dep]?.push(role)
    return acc
  }, {})
  // unwraps record for better type interference
  return Object.entries(departments) as [Department, UserRole[]][]
})
</script>
